import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import Skeleton from 'react-loading-skeleton'
import { trackEvent } from 'analytics/trackFunctions'

import { useLoanInfoStore } from 'store'

import { CREDIT_PRODUCT, getCreditHistory } from 'api/advances'

import CustomListbox from 'components/listbox'
import LoanDetailButton from 'components/AdvanceDetails'
import EmptySection, { SECTION_TEXTS } from 'components/EmptySection'

const LISTBOX_STATES = [
  {
    key: 'active',
    name: 'Activos'
  },
  {
    key: 'finalized',
    name: 'Finalizados'
  }
]

const CreditHistory = ({
  product
}: {
  product: keyof typeof CREDIT_PRODUCT
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [lastPage, setLastPage] = useState<number>(null)
  const [credit, setCredit] = useState([])
  const [state, setState] = useState(LISTBOX_STATES[0])
  const [isLoading, setIsLoading] = useState(true)

  const observer = useRef(null)

  const { updateLoanInfo } = useLoanInfoStore()

  const navigate = useNavigate()

  function getHistory(page, states) {
    return getCreditHistory({
      page,
      creditProduct: product,
      states
    })
  }

  useEffect(() => {
    setIsLoading(true)
    trackEvent({ eventName: 'bille_boton_filtrar' })
    setCurrentPage(1)
    setCredit([])
    getHistory(1, state.key)
      .then((res) => {
        setLastPage(res.lastPage)
        setCredit(res.data)
      })
      .finally(() => setIsLoading(false))
  }, [state])

  useEffect(() => {
    if (isLoading) return
    if (currentPage <= lastPage) {
      setIsLoading(true)
      getHistory(currentPage, state.key)
        .then((res) => {
          setCredit((prevCredit) => [...prevCredit, ...res.data])
        })
        .finally(() => setIsLoading(false))
    }
  }, [currentPage])

  const emptySectionText: () => keyof typeof SECTION_TEXTS = () => {
    const productSlug = product === 'WALLET' ? 'ADVANCES' : 'LOANS'

    return state == LISTBOX_STATES[0]
      ? `ACTIVE_${productSlug}`
      : `PAID_${productSlug}`
  }

  const lastButtonRef = useCallback(
    (node) => {
      if (isLoading) return
      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && currentPage <= lastPage) {
          // trigger loading a new page
          setCurrentPage((prevPage) => prevPage + 1)
        }
      })

      if (node) observer.current.observe(node)
    },
    [isLoading]
  )

  const renderContent = () => {
    if (credit && credit.length > 0) {
      return credit.map((item, index) => (
        <div
          className='w-full'
          key={item.uuid + index || index}
          ref={credit.length === index + 1 ? lastButtonRef : null}
        >
          <LoanDetailButton
            action={() => {
              updateLoanInfo(item)
              navigate('/wayni-credito/detalle')
            }}
            loanData={item}
          />
        </div>
      ))
    }

    if (credit && credit.length === 0 && !isLoading)
      return <EmptySection section={emptySectionText()} />
  }

  return (
    <>
      <CustomListbox
        list={LISTBOX_STATES}
        selectedValue={state}
        setSelectedValue={setState}
        className='rounded-2xl max-w-[7.5rem] text-2xs !font-bold h-9 pr-2 pl-4'
        listClassName='max-w-[7.5rem] text-2xs font-bold'
      />
      {renderContent()}
      {isLoading && <LoadingSkeletons />}
    </>
  )
}

export default CreditHistory

const LoadingSkeletons = () => (
  <>
    <Skeleton height={68} className='rounded-lg mt-3' />
    <Skeleton height={68} className='rounded-lg mt-3' />
    <Skeleton height={68} className='rounded-lg mt-3' />
  </>
)
